import { FC } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IntervalsModelType, TransportModelType } from "../../../../../common/modelTypes";
import { colGrow } from "../../../../../common/utils";
import ComparisonLinearProgress from "../../../../../components/charts/graph/ComparisonLinearProgress";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import ContentLocked from "../../../../../components/common/ContentLocked";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../../../components/common/PermissionGridItem";
import {
	EmissionsIntervalResultsDTO,
	EmissionsTransportsResultsDatumDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { getLockedContents } from "../../../../../permissions/permissionUtils";
import EmissionsIntervals from "../../../components/ResultsEmissionsIntervals";

interface Props {
	transportEmissions: TransportModelType;
	keyEmissions: string;
	emissionsIntervals: IntervalsModelType;
	emissionsIntervalsPeriodType: "month" | "quarter" | "four-month" | "semester";
	setEmissionsIntervalsPeriodType: (periodType: "month" | "quarter" | "four-month" | "semester") => void;
	intervalMode: "emissions" | "energy";
	onIntervalModeChange: (mode: "emissions" | "energy") => void;
}

type EmissionType = keyof Pick<EmissionsTransportsResultsDatumDTO, "co2Ttw" | "co2Wtw">;
type EmissionKey = keyof EmissionsIntervalResultsDTO;
type IntensityKey = "intensityTtw" | "intensityWtw";

const Overview: FC<Props> = ({
	transportEmissions,
	keyEmissions,
	emissionsIntervals,
	emissionsIntervalsPeriodType,
	setEmissionsIntervalsPeriodType,
	intervalMode,
	onIntervalModeChange,
}) => {
	const { t } = useTranslation("result.transport");

	const intensityKey: IntensityKey = keyEmissions === "co2Ttw" ? "intensityTtw" : "intensityWtw";

	// Select emission for all the page
	const dropdownSelectEmissions = [
		{ key: "co2Ttw", label: t("common:ttw select") },
		{ key: "co2Wtw", label: t("common:wtw select") },
	];

	/**
	 * Resolve which key to use for the interval chart
	 * Energy → energyTtw
	 * Emissions → emission selected in overview
	 */
	const resolvedIntervalKey: EmissionKey = intervalMode === "energy" ? "energyTtw" : (keyEmissions as EmissionKey);

	// Prepare data for the chart
	const chartData = emissionsIntervals.data && keyEmissions ? emissionsIntervals.data[resolvedIntervalKey].data : [];
	const chartMeasure =
		emissionsIntervals.data && keyEmissions ? emissionsIntervals.data[resolvedIntervalKey].measure : "";

	// Calculate from and to percentages for the ComparisonLinearProgress component
	const totalEmissions = transportEmissions?.data?.total?.current?.[keyEmissions as EmissionType]?.value || 0;
	const fromEmissions = transportEmissions?.data?.greenRouter?.current[keyEmissions as EmissionType]?.value || 0;
	const toEmissions = transportEmissions?.data?.thirdParty?.current[keyEmissions as EmissionType]?.value || 0;

	const fromPercent = totalEmissions !== 0 ? (fromEmissions / totalEmissions) * 100 : 0;
	const toPercent = totalEmissions !== 0 ? (toEmissions / totalEmissions) * 100 : 0;

	const fromEmissionsDatasource = {
		name: "GreenRouter",
		value: fromPercent,
	};

	const toEmissionsDatasource = {
		name: t("third parties"),
		value: toPercent,
	};

	const isWtw = keyEmissions === "co2Wtw";

	const marketBase: MarketBaseType = {
		emissions: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtw
			: transportEmissions.data?.total?.current.marketBasedTtw,
		delta: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtwDelta
			: transportEmissions.data?.total?.current.marketBasedTtwDelta,
		coverage: isWtw
			? transportEmissions.data?.total?.current.marketBasedWtwCoverage
			: transportEmissions.data?.total?.current.marketBasedTtwCoverage,
		isLoading: transportEmissions.isLoading,
		onReload: transportEmissions.reload,
	};

	const titles = {
		totalEmissions: t("total transport emissions"),
		energy: t("energy consumption"),
		intensity: t("intensity emission"),
		// card title of the intervals chart (set by EmissionsIntervals)
		intervals: t("feature.units:overview/consumption trends and details"),
		grossWeight: t("gross weight"),
		tonsKm: t("tons km"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [transportEmissions],
			names: [
				titles.totalEmissions,
				titles.energy,
				titles.intensity,
				titles.grossWeight,
				titles.tonsKm,
				titles.marketBased,
			],
		},
		{ models: [emissionsIntervals], names: [titles.intervals] },
	]);

	return (
		<Grid container spacing={2}>
			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} minHeight={180}>
					<NumericValueWidget
						title={titles.totalEmissions}
						delta={transportEmissions?.data?.total?.delta?.[keyEmissions as EmissionType]}
						value={transportEmissions?.data?.total?.current[keyEmissions as EmissionType]?.value}
						measure={transportEmissions?.data?.total?.current[keyEmissions as EmissionType]?.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
						graph={<ComparisonLinearProgress from={fromEmissionsDatasource} to={toEmissionsDatasource} />}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} minHeight={180}>
					<NumericValueWidget
						title={titles.energy}
						delta={transportEmissions?.data?.total?.delta.energyTtw}
						value={transportEmissions?.data?.total?.current.energyTtw.value}
						measure={transportEmissions?.data?.total?.current.energyTtw.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
						coverage={transportEmissions.data?.total?.current.energyTtwCoverage}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} minHeight={180}>
					<NumericValueWidget
						title={titles.intensity}
						delta={transportEmissions?.data?.total?.delta[intensityKey]}
						value={transportEmissions?.data?.total?.current[intensityKey].value}
						measure={transportEmissions?.data?.total?.current[intensityKey].measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[emissionsIntervals]} size={12} minHeight={450}>
				<EmissionsIntervals
					selectTitle={t("trend")}
					periodType={emissionsIntervalsPeriodType}
					setPeriodType={setEmissionsIntervalsPeriodType}
					isLoading={emissionsIntervals.isLoading}
					onReload={emissionsIntervals.reload}
					chartData={chartData}
					chartMeasure={chartMeasure}
					menuOptions={dropdownSelectEmissions}
					intervalMode={intervalMode}
					onIntervalModeChange={onIntervalModeChange}
				/>
			</PermissionGridItem>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.grossWeight}
						value={transportEmissions?.data?.total?.current.weight.value}
						measure={transportEmissions?.data?.total?.current.weight.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[transportEmissions]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.tonsKm}
						value={transportEmissions?.data?.total?.current.tkm.value}
						measure={transportEmissions?.data?.total?.current.tkm.measure}
						isLoading={transportEmissions.isLoading}
						onReload={transportEmissions.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[transportEmissions]} size={12}>
				<MarketBaseWidget marketBase={marketBase} />
			</PermissionGridItem>

			{lockedContents.length > 0 && (
				<Grid size={12}>
					<ContentLocked items={lockedContents} />
				</Grid>
			)}
		</Grid>
	);
};

export default Overview;
